<script setup>
import FsymPicker from '@/pages/manage/providers/sec/ownership/filings/FsymPicker.vue'
import {
    ownershipFilingsGet,
    ownershipFilingsPut,
    setFsymIds,
} from '@/pages/manage/providers/sec/ownership/filings/services.js'
import { asyncComputed } from '@/utils/asyncComputed.js'
import { computed, inject, ref } from 'vue'
import { request } from '@/utils/request.js'
import FactSetIssuerPicker from '@/pages/manage/providers/sec/ownership/filings/FactSetIssuerPicker.vue'
import SingleTaskStatus from './SingleTaskStatus.vue'

const props = defineProps({
    item: {
        type: Object,
        required: true,
    },
})

const filing = asyncComputed(() => ownershipFilingsGet(props.item.secondaryEntityId))

const securities = computed(() => {
    if (!filing.value) {
        return []
    }

    const map = {}

    filing.value.records.forEach(r => {
        if (r.transactionCode !== 'P' && r.transactionCode !== 'S') {
            return
        }

        const footnotes = [
            ...new Set([
                ...(r.securityTitleFootnotes ?? []),
                ...(r.transactionSharesFootnotes ?? []),
                ...(r.underlyingSecuritySharesFootnotes ?? []),
                ...(r.underlyingSecurityTitleFootnotes ?? []),
            ]),
        ]

        ;(map[r.securityTitle] ??= []).push({
            securityTitle: r.securityTitle,
            fsymId: r.fsymId,
            footnotes,
            record: r,
        })
        if (r.isDerivative && r.underlyingSecurityTitle) {
            ;(map[r.underlyingSecurityTitle] ??= []).push({
                securityTitle: r.underlyingSecurityTitle,
                fsymId: r.underlyingSecurityFsymId,
                footnotes,
                record: r,
            })
        }
    })

    return Object.entries(map).map(([title, records]) => {
        const fsymIds = new Set(records.map(r => r.fsymId))
        let fsymId = [...fsymIds].join('; ')
        if (fsymIds.size > 1) {
            fsymId = 'Conflicting: ' + fsymId
        }

        return {
            conflict: fsymIds.size > 1,
            securityTitle: title,
            fsymId,
            footnotes: [...new Set(records.flatMap(r => r.footnotes))],
            records,
        }
    })
})

const footnotes = computed(() => {
    const uniq = new Set(securities.value.flatMap(s => s.footnotes))

    if (uniq.size == 0) {
        return []
    }

    return filing.value.footnotes.filter(f => uniq.has(f.id))
})

const securityTypes = new Set(['SHARE', 'MF_C', 'MF_O', 'PREF'])

const fsyms = asyncComputed(async () => {
    let options = []

    if (filing.value?.issuerFactSetEntityId) {
        options =
            (await request.get(`/api/providers/factset/fsyms/entities/${filing.value.issuerFactSetEntityId}`)) ?? []
        options = options
            .filter(fsym => securityTypes.has(fsym.securityType))
            .map(fsym => ({ ...fsym, display: format(fsym) }))
    }

    securities.value.forEach(t => {
        if (t.fsymId && !options.find(fsym => fsym.fsymId === t.fsymId)) {
            options.push({
                fsymId: t.fsymId,
                display: `<unknown> [${t.fsymId}]`,
                name: '',
            })
        }
    })

    options.sort((lhs, rhs) => lhs.name.localeCompare(rhs.name))

    return options

    function format(fsym) {
        if (fsym.symbol) {
            return `${fsym.symbol} | ${fsym.name} [${fsym.fsymId}, ${fsym.securityType}]`
        } else {
            return `${fsym.name} [${fsym.fsymId}, ${fsym.securityType}]`
        }
    }
}, [])

const saving = ref(false)

const updating = computed(() => saving.value || filing.updating)

const flash = inject('flash')

const issuerFactSetEntityId = computed({
    get: () => filing.value.issuerFactSetEntityId,
    set: async value => {
        try {
            saving.value = true
            const { ok, content } = await ownershipFilingsPut(filing.value.accessionNumber, {
                ...filing.value,
                issuerFactSetEntityId: value,
            })

            if (ok) {
                filing.value = content
                flash('Updated issuer', 'success')
                return
            }
        } catch {
        } finally {
            saving.value = false
        }

        flash('Update failed', 'error')
    },
})

async function setFsymId(securityTitle, fsymId) {
    fsymId ||= null

    try {
        saving.value = true
        const { ok } = await setFsymIds(filing.value.accessionNumber, {
            [securityTitle]: fsymId,
        })

        if (ok) {
            filing.value.records.forEach(t => {
                if (t.securityTitle === securityTitle) {
                    t.fsymId = fsymId
                }

                if (t.underlyingSecurityTitle === securityTitle) {
                    t.underlyingSecurityFsymId = fsymId
                }
            })

            setTimeout(status.value.refresh, 1000)

            flash('Updated fsymId', 'success')

            return
        }
    } catch {
    } finally {
        saving.value = false
    }

    flash('Update failed', 'error')
}

const status = ref(null)
</script>
<template>
    <div class="loading-container" style="height: 100%">
        <loading :active="updating" :is-full-page="false" />
        <div class="task-body" v-if="filing">
            <router-link
                :to="`/manage/providers/sec/ownership/filings/${filing.accessionNumber}`"
                target="_blank"
                class="button primary"
                v-if="filing"
            >
                View Filing
            </router-link>

            <h2>
                <SingleTaskStatus :taskId="item.id" :seconds="300" ref="status" />
                <span>SEC Ownership</span>
            </h2>

            <dl>
                <dt>Issuer</dt>
                <dd>{{ filing.issuerName }}</dd>
                <dt>FactSet Entity</dt>
                <dd>
                    <FactSetIssuerPicker :cik="filing.issuerCik" :edit="true" v-model="issuerFactSetEntityId" />
                </dd>
            </dl>

            <table class="mt-table">
                <tr>
                    <th>Security Title</th>
                    <th>FsymId</th>
                    <th v-if="footnotes.length > 0"></th>
                </tr>
                <tr v-for="security in securities" :key="security.securityTitle">
                    <td>{{ security.securityTitle }}</td>
                    <td>
                        <FsymPicker
                            :fsyms="fsyms"
                            :value="security.fsymId"
                            @update:value="fsym => setFsymId(security.securityTitle, fsym)"
                        />
                    </td>
                    <td v-if="footnotes.length > 0">
                        {{ security.footnotes.join(', ') }}
                    </td>
                </tr>
            </table>

            <section v-if="footnotes?.length">
                <h3>Footnotes</h3>
                <ul>
                    <li v-for="footnote in footnotes" :key="footnote.id">{{ footnote.id }}: {{ footnote.value }}</li>
                </ul>
            </section>
        </div>
    </div>
</template>
<style scoped>
td,
th {
    width: 50%;
}

td:last-child,
th:last-child {
    width: 0;
}

h2 {
    align-items: center;
    display: flex;
    gap: 8px;
    justify-content: flex-start;
}
</style>
